var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/* eslint-disable @next/next/no-css-tags */
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, IntlProvider, useIntl } from "react-intl";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import css from "!css-loader!postcss-loader!./globals.css";
import { CheckCircleIcon, CheckIcon, StopIcon, TrashIcon, VideoCameraIcon, } from "@heroicons/react/outline";
import { cn } from "../lib/utils";
import { useLocalStorage } from "@uidotdev/usehooks";
import { getUserEmail, sendReport, startScreenRecording, } from "./bindings/bugpilot";
import { RibbonLabel } from "./RibbonLabel";
var MOBILE_STYLES = "max-md:top-1/2 max-md:transform max-md:-translate-y-1/2 max-md:left-1/2 max-md:transform max-md:-translate-x-1/2 max-md:max-w-viewport";
var POSITION_STYLES = {
    "center-right": "".concat(MOBILE_STYLES, " md:right-12 md:top-1/2 md:transform md:-translate-y-1/2"),
    right: "".concat(MOBILE_STYLES, " md:right-12 md:top-1/2 md:transform md:-translate-y-1/2"),
    left: "".concat(MOBILE_STYLES, " md:left-12 md:top-1/2 md:transform md:-translate-y-1/2"),
    bottom: "".concat(MOBILE_STYLES, " md:bottom-12 md:left-1/2 md:transform md:-translate-x-1/2"),
    top: "".concat(MOBILE_STYLES, " md:top-12 md:left-1/2 md:transform md:-translate-x-1/2"),
};
export function BugForm(_a) {
    var _b = _a.anchorSelector, anchorSelector = _b === void 0 ? null : _b, _c = _a.ribbonLabel, ribbonLabel = _c === void 0 ? "Report bug" : _c, _d = _a.primaryColor, primaryColor = _d === void 0 ? "#000000" : _d, _e = _a.secondaryColor, secondaryColor = _e === void 0 ? "#FFFFFF" : _e, _f = _a.descriptionRequired, descriptionRequired = _f === void 0 ? true : _f, _g = _a.recordingRequired, recordingRequired = _g === void 0 ? false : _g, _h = _a.emailRequired, emailRequired = _h === void 0 ? true : _h, _j = _a.anchorPosition, anchorPosition = _j === void 0 ? "center-right" : _j;
    var _k = useLocalStorage("io.bugpilot.widget.uistate", "minimized"), state = _k[0], setState = _k[1];
    var _l = useLocalStorage("io.bugpilot.widget.description"), description = _l[0], setDescription = _l[1];
    var _m = useLocalStorage("io.bugpilot.widget.email"), email = _m[0], setEmail = _m[1];
    var _o = useLocalStorage("io.bugpilot.widget.hasrecording", false), hasScreenRecording = _o[0], setHasScreenRecording = _o[1];
    var _p = useState(false), submitting = _p[0], setSubmitting = _p[1];
    var intl = useIntl();
    useEffect(function () {
        // when widget is expanded, fetch user email and update state
        if (state !== "expanded") {
            return;
        }
        var email = getUserEmail();
        if (email) {
            setEmail(email);
        }
    }, [setEmail, state]);
    var handleSendReportClick = function (event) {
        event.preventDefault();
        if (!description && descriptionRequired) {
            alert("Please describe the issue");
            return;
        }
        if (!email && emailRequired) {
            alert("Please enter your email");
            return;
        }
        if (!hasScreenRecording && recordingRequired) {
            alert("Attaching a screen recording is required to submit this report");
            return;
        }
        setSubmitting(true);
        sendReport({
            email: email,
            description: description,
        });
        // test: set success
        setTimeout(function () {
            setState("success");
        }, 2 * 1000);
        setTimeout(function () {
            setSubmitting(false);
            handleResetForm();
        }, 7 * 1000);
    };
    var handleWidgetClick = function () {
        if (state === "minimized") {
            setState("expanded");
        }
        else {
            setState("minimized");
        }
    };
    var handleStartRecordingClick = function () {
        setState("recording");
        startScreenRecording();
    };
    var handleStopRecordingClick = function () {
        setState("expanded");
        setHasScreenRecording(true);
    };
    var handleResetForm = function () {
        setState("minimized");
        setDescription("");
        setEmail("");
        setHasScreenRecording(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("style", null, css.toString()),
        React.createElement(RibbonLabel, { attachedToExistingSelector: anchorSelector, ribbonLabel: ribbonLabel, handleWidgetClick: handleWidgetClick, primaryColor: primaryColor, secondaryColor: secondaryColor, anchorPosition: anchorPosition }),
        state === "expanded" || state === "recording" ? (React.createElement("div", { className: cn("font-sans fixed w-fit h-fit select-none transition-all duration-300 pointer-events-none ".concat(POSITION_STYLES[anchorPosition]), {
                "max-md:opacity-20 max-md:hover:opacity-80": state === "recording",
            }), id: "bugpilot-popup-expanded" },
            React.createElement("div", { className: cn("bg-white text-black rounded-lg shadow-lg  mx-auto p-6 space-y-6 animate-fade-in-left relative overflow-visible flex flex-col items-start justify-center pointer-events-auto ml-auto transition-size duration-300", {
                    "max-w-screen-sm md:max-w-sm w-80": state !== "recording",
                    "w-fit p-3": state === "recording",
                }) },
                React.createElement("form", { onSubmit: handleSendReportClick, className: "w-full flex flex-col justify-center items-stretch gap-4" },
                    React.createElement("div", { className: cn({ hidden: state === "recording" }) },
                        React.createElement(Textarea, { className: "min-h-[100px]", id: "description", placeholder: intl.formatMessage({ id: "cslouU", defaultMessage: "What's not working?" }), disabled: submitting, value: description, onChange: function (event) { return setDescription(event.target.value); }, required: Boolean(descriptionRequired) })),
                    React.createElement("div", { className: cn({ hidden: state === "recording" }) },
                        React.createElement(Input, { id: "email", placeholder: intl.formatMessage({ id: "BFS/cx", defaultMessage: "Enter your email" }), type: "email", disabled: submitting, value: email, onChange: function (event) { return setEmail(event.target.value); }, required: Boolean(emailRequired) })),
                    React.createElement("div", null, state === "recording" ? (React.createElement(Button, { type: "reset", variant: "secondary", onClick: handleStopRecordingClick, size: "sm" },
                        React.createElement(StopIcon, { className: "mr-2 h-4 w-4 text-red-500 animate-pulse" }),
                        React.createElement(FormattedMessage, { id: 'lI9Ga6', defaultMessage: 'Stop recording' }))) : hasScreenRecording ? (React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Button, { type: "reset", variant: "outline", onClick: handleStartRecordingClick, size: "sm", disabled: true },
                            React.createElement(CheckIcon, { className: "mr-2 h-4 w-4" }),
                            React.createElement(FormattedMessage, { id: 'YyylUC', defaultMessage: 'Recording attached' })),
                        React.createElement(Button, { type: "reset", size: "icon", variant: "link", onClick: function () { return setHasScreenRecording(false); }, className: "text-gray-300 hover:text-red-500", disabled: submitting },
                            React.createElement(TrashIcon, { className: "mr-2 h-4 w-4" })))) : (React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Button, { type: "reset", variant: "secondary", onClick: handleStartRecordingClick, size: "sm", disabled: submitting },
                            React.createElement(VideoCameraIcon, { className: "mr-2 h-4 w-4" }),
                            React.createElement(FormattedMessage, { id: 'Dl9DeQ', defaultMessage: 'Record your screen' })),
                        React.createElement("span", { className: "text-red-500" }, recordingRequired ? " *" : "")))),
                    React.createElement("div", { className: cn("flex gap-2", { hidden: state === "recording" }) },
                        React.createElement(Button, { type: "submit", variant: "default", size: "sm", loading: submitting, className: "w-fit min-w-[6rem] rounded-sm", style: {
                                backgroundColor: primaryColor || undefined,
                            } },
                            React.createElement(FormattedMessage, { id: 'oDYDJa', defaultMessage: 'Send report' })),
                        React.createElement(Button, { type: "reset", variant: "ghost", size: "sm", onClick: handleResetForm, disabled: submitting, className: "" },
                            React.createElement(FormattedMessage, { id: 'Waxu2T', defaultMessage: 'Cancel' }))))))) : null,
        state === "success" ? (React.createElement("div", { className: "font-sans fixed w-fit h-fit select-none transition-all duration-300 pointer-events-none ".concat(POSITION_STYLES[anchorPosition]), id: "bugpilot-popup-success" },
            React.createElement("div", { className: cn("bg-white text-black rounded-lg shadow-lg  mx-auto p-6 space-y-3 animate-fade-in-left relative overflow-visible flex flex-col items-start justify-center pointer-events-auto ml-auto transition-size duration-300", {
                    "max-w-screen-sm md:max-w-sm w-80": true,
                }) },
                React.createElement(CheckCircleIcon, { className: "h-8 w-8 text-green-500" }),
                React.createElement("p", { className: "text-sm" },
                    React.createElement(FormattedMessage, { id: 'w75S1e', defaultMessage: 'Thank you!' }))))) : null,
        state === "recording" ? (
        // display a recording red frame around the screen
        React.createElement("div", { className: "font-sans fixed inset-0 border-4 md:border-8 border-red-600 pointer-events-none animate-pulse", id: "bugpilot-recording-inset" })) : null));
}
function loadLocaleData(locale) {
    switch (locale) {
        case "es":
        case "es-ES":
            // @ts-ignore
            return import("../lang/compiled/es-ES.json");
        case "it":
        case "it-IT":
            // @ts-ignore
            return import("../lang/compiled/it-IT.json");
        case "fr":
        case "fr-FR":
            // @ts-ignore
            return import("../lang/compiled/fr-FR.json");
        case "pt":
        case "pt-BR":
            // @ts-ignore
            return import("../lang/compiled/pt-BR.json");
        case "mk":
        case "mk-MK":
            // @ts-ignore
            return import("../lang/compiled/mk-MK.json");
        case "de":
        case "de-DE":
            // @ts-ignore
            return import("../lang/compiled/de-DE.json");
        case "uk":
        case "uk-UA":
            // @ts-ignore
            return import("../lang/compiled/uk-UA.json");
        case "pl":
        case "pl-PL":
            // @ts-ignore
            return import("../lang/compiled/pl-PL.json");
        default:
            // @ts-ignore
            return import("../lang/compiled/en.json");
    }
}
export var BugFormWithTranslations = function (props) {
    var _a = useState({}), messages = _a[0], setMessages = _a[1];
    var locale = useMemo(function () { var _a; return props.preferredLocale || ((_a = window === null || window === void 0 ? void 0 : window.navigator) === null || _a === void 0 ? void 0 : _a.language) || "en"; }, [props.preferredLocale]);
    useEffect(function () {
        loadLocaleData(locale).then(function (data) {
            setMessages(data.default);
        });
    }, [locale]);
    if (!messages) {
        return null;
    }
    return (React.createElement(IntlProvider, { locale: locale, defaultLocale: "en", messages: messages },
        React.createElement(BugForm, __assign({}, props))));
};
